import { computed, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import type { BacktestJobRecord, BacktestJobsSummary } from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { backtestJobsListPollingIntervalMs } from 'src/lib/featureFlags/flags/backtestJobsListPollingIntervalMs';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { getCancelBacktestJobMutation, getDeleteBacktestJobMutation } from 'src/queries/backtestSpecs';
// eslint-disable-next-line no-restricted-imports -- legacy direct import
import { getBacktestJobs, getBacktestJobsSummary } from 'src/queries/http/backtestSpecs';
import { isTerminalBacktestJobStatus } from 'src/routes/widgets/BacktestWidget/components/statusPresentation';
import type { ReactBindings } from 'src/types/bindings';

const DEFAULT_PAGE_SIZE = 50;

interface JobsListState {
    readonly jobs: readonly BacktestJobRecord[];
    readonly page: number;
    readonly pageSize: number;
    readonly totalCount: number;
    readonly summary: BacktestJobsSummary | null;
    readonly loading: boolean;
    readonly polling: boolean;
    /**
     * Ids of jobs whose DELETE is in flight. Used to filter poll responses so
     * a stale poll cannot resurrect an optimistically-removed row.
     */
    readonly pendingDeletes: ReadonlySet<string>;
    /**
     * Ids of jobs whose CANCEL is in flight. Used to keep an optimistic
     * `'cancelled'` status pinned across poll responses that still report the
     * job as running.
     */
    readonly pendingCancels: ReadonlySet<string>;
}

type JobsListAction =
    | { type: 'START_POLLING' }
    | { type: 'STOP_POLLING' }
    | { type: 'SET_PAGE'; page: number }
    | { type: 'FETCH_STARTED' }
    | { type: 'FETCH_SUCCEEDED'; jobs: readonly BacktestJobRecord[]; totalCount: number }
    | { type: 'FETCH_FAILED' }
    | { type: 'SUMMARY_SUCCEEDED'; summary: BacktestJobsSummary }
    | { type: 'JOB_DELETE_OPTIMISTIC'; id: string }
    | { type: 'JOB_DELETE_SUCCEEDED'; id: string }
    | { type: 'JOB_DELETE_FAILED'; id: string }
    | { type: 'JOB_CANCEL_OPTIMISTIC'; id: string }
    | { type: 'JOB_CANCEL_SUCCEEDED'; id: string }
    | { type: 'JOB_CANCEL_FAILED'; id: string };

function withoutId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
    if (!ids.has(id)) return ids;
    const next = new Set(ids);
    next.delete(id);
    return next;
}

function jobsListReducer(state: JobsListState, action: JobsListAction): JobsListState {
    switch (action.type) {
        case 'START_POLLING':
            return { ...state, polling: true };
        case 'STOP_POLLING':
            return { ...state, polling: false };
        case 'SET_PAGE':
            // Clear the visible rows so the in-flight fetch shows the loading state
            // instead of stale page contents while the new page is loading.
            return { ...state, page: action.page, jobs: [] };
        case 'FETCH_STARTED':
            // Only show loading indicator when there's nothing to display — either
            // initial fetch or after a page change clears the rows.
            if (state.jobs.length > 0) return state;
            return { ...state, loading: true };
        case 'FETCH_SUCCEEDED': {
            // Filter out jobs whose delete is in flight so a stale poll cannot
            // resurrect an optimistically-removed row, then patch any jobs whose
            // cancel is in flight to keep their optimistic `'cancelled'` pill.
            let jobs: readonly BacktestJobRecord[] = action.jobs;
            if (state.pendingDeletes.size > 0) {
                jobs = jobs.filter((j) => !state.pendingDeletes.has(j.id));
            }
            if (state.pendingCancels.size > 0) {
                jobs = jobs.map((j) => (state.pendingCancels.has(j.id) ? { ...j, status: 'cancelled' } : j));
            }
            return { ...state, jobs, totalCount: action.totalCount, loading: false };
        }
        case 'FETCH_FAILED':
            return { ...state, loading: false };
        case 'SUMMARY_SUCCEEDED':
            return { ...state, summary: action.summary };
        case 'JOB_DELETE_OPTIMISTIC': {
            const pendingDeletes = new Set(state.pendingDeletes);
            pendingDeletes.add(action.id);
            return {
                ...state,
                jobs: state.jobs.filter((j) => j.id !== action.id),
                pendingDeletes,
            };
        }
        case 'JOB_DELETE_SUCCEEDED':
            return { ...state, pendingDeletes: withoutId(state.pendingDeletes, action.id) };
        case 'JOB_DELETE_FAILED':
            // Do not re-insert from a snapshot — let the follow-up fetch
            // reconcile from the server. Snapshot-restore would duplicate the
            // row if a poll had already brought it back.
            return { ...state, pendingDeletes: withoutId(state.pendingDeletes, action.id) };
        case 'JOB_CANCEL_OPTIMISTIC': {
            const pendingCancels = new Set(state.pendingCancels);
            pendingCancels.add(action.id);
            return {
                ...state,
                jobs: state.jobs.map((j) => (j.id === action.id ? { ...j, status: 'cancelled' } : j)),
                pendingCancels,
            };
        }
        case 'JOB_CANCEL_SUCCEEDED':
            return { ...state, pendingCancels: withoutId(state.pendingCancels, action.id) };
        case 'JOB_CANCEL_FAILED':
            // Same pattern as delete: clear the mark and let the reconciling
            // fetch restore whatever the server actually reports.
            return { ...state, pendingCancels: withoutId(state.pendingCancels, action.id) };
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Owns the paged jobs list, its background polling, and a server-computed
 * summary (status counts + best/worst PnL) that always reflects the user's
 * whole visible set, independent of the page being viewed.
 *
 * Polling is driven imperatively via `startPolling()` / `stopPolling()` —
 * the widget impl wires these to viewMode transitions. The current run's
 * active job, isRunning flag, and results stay on the widget impl.
 */
export class BacktestJobsListModel implements Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #pollBox: DisposableBox;
    readonly #featureFlagService: ReactBindings['FeatureFlagService'];
    readonly #logger: ReactBindings['Logger'];

    readonly #state: Signal<JobsListState>;
    readonly #deleteJobMutation: MutationObserver<void, Error, string>;
    readonly #cancelJobMutation: MutationObserver<BacktestJobRecord, Error, string>;

    readonly jobs: ReadonlySignal<readonly BacktestJobRecord[]>;
    readonly page: ReadonlySignal<number>;
    readonly pageSize: ReadonlySignal<number>;
    readonly totalCount: ReadonlySignal<number>;
    readonly summary: ReadonlySignal<BacktestJobsSummary | null>;
    readonly loading: ReadonlySignal<boolean>;

    constructor(
        featureFlagService: ReactBindings['FeatureFlagService'],
        logger: ReactBindings['Logger'],
        queryClient: ReactBindings['QueryClient'],
    ) {
        this.#featureFlagService = featureFlagService;
        this.#logger = logger;
        this.#pollBox = this.#disposableStack.use(new DisposableBox());

        this.#state = signal<JobsListState>({
            jobs: [],
            page: 0,
            pageSize: DEFAULT_PAGE_SIZE,
            totalCount: 0,
            summary: null,
            loading: false,
            polling: false,
            pendingDeletes: new Set(),
            pendingCancels: new Set(),
        });
        this.jobs = computed(() => this.#state.value.jobs);
        this.page = computed(() => this.#state.value.page);
        this.pageSize = computed(() => this.#state.value.pageSize);
        this.totalCount = computed(() => this.#state.value.totalCount);
        this.summary = computed(() => this.#state.value.summary);
        this.loading = computed(() => this.#state.value.loading);

        this.#deleteJobMutation = new MutationObserver(queryClient, getDeleteBacktestJobMutation(queryClient));
        this.#cancelJobMutation = new MutationObserver(queryClient, getCancelBacktestJobMutation(queryClient));
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    #dispatch(action: JobsListAction): void {
        this.#state.value = jobsListReducer(this.#state.value, action);
    }

    startPolling(): void {
        if (untracked(() => this.#state.value.polling)) return;
        this.#dispatch({ type: 'START_POLLING' });
        void this.#fetch();
    }

    stopPolling(): void {
        if (!untracked(() => this.#state.value.polling)) return;
        this.#dispatch({ type: 'STOP_POLLING' });
        this.#pollBox.clear();
    }

    setPage(page: number): void {
        if (untracked(() => this.#state.value.page === page)) return;
        // Cancel the pending poll so it doesn't race the page-change fetch.
        this.#pollBox.clear();
        this.#dispatch({ type: 'SET_PAGE', page });
        void this.#fetch();
    }

    /**
     * Optimistically remove a job and call the delete endpoint. After settle
     * (success or failure) an immediate fetch reconciles the page boundary
     * (the next row shifts up from the following page) and the summary counts.
     */
    async deleteJob(id: string): Promise<void> {
        if (!untracked(() => this.#state.value.jobs.some((j) => j.id === id))) return;
        this.#dispatch({ type: 'JOB_DELETE_OPTIMISTIC', id });
        try {
            await this.#deleteJobMutation.mutate(id);
            this.#dispatch({ type: 'JOB_DELETE_SUCCEEDED', id });
            void this.#fetch();
        } catch (e) {
            this.#dispatch({ type: 'JOB_DELETE_FAILED', id });
            this.#logger.error('Failed to delete backtest job', {
                jobId: id,
                error: e instanceof Error ? e.message : String(e),
            });
            void this.#fetch();
            throw e;
        }
    }

    /**
     * Optimistically flip a job's status to `'cancelled'` and call the cancel
     * endpoint. No-op if the job isn't present or is already terminal. On both
     * success and failure, trigger an immediate fetch so the server-computed
     * summary counts (runningCount, cancelledCount) reconcile — they're no
     * longer derived from the in-memory jobs list.
     */
    async cancelJob(id: string): Promise<void> {
        const job = untracked(() => this.#state.value.jobs.find((j) => j.id === id));
        if (!job) return;
        if (isTerminalBacktestJobStatus(job.status)) return;
        this.#dispatch({ type: 'JOB_CANCEL_OPTIMISTIC', id });
        try {
            await this.#cancelJobMutation.mutate(id);
            this.#dispatch({ type: 'JOB_CANCEL_SUCCEEDED', id });
            void this.#fetch();
        } catch (e) {
            this.#dispatch({ type: 'JOB_CANCEL_FAILED', id });
            this.#logger.error('Failed to cancel backtest job', {
                jobId: id,
                error: e instanceof Error ? e.message : String(e),
            });
            void this.#fetch();
            throw e;
        }
    }

    async #fetch(): Promise<void> {
        this.#dispatch({ type: 'FETCH_STARTED' });
        const { page, pageSize } = untracked(() => this.#state.value);
        const params: Record<string, string> = {
            $top: String(pageSize),
            $skip: String(page * pageSize),
            $count: 'true',
            $orderby: 'createdAt desc',
        };

        try {
            const [pageResult, summary] = await Promise.all([getBacktestJobs(params), getBacktestJobsSummary()]);
            this.#dispatch({
                type: 'FETCH_SUCCEEDED',
                jobs: pageResult.results,
                totalCount: pageResult.count,
            });
            this.#dispatch({ type: 'SUMMARY_SUCCEEDED', summary });

            if (!untracked(() => this.#state.value.polling)) return;
            // Schedule based on the whole-set summary so polling continues for
            // in-progress jobs on pages the user isn't currently viewing.
            if (summary.runningCount > 0) {
                this.#scheduleNextPoll();
            } else {
                this.#pollBox.clear();
            }
        } catch (e) {
            this.#logger.error('Failed to fetch backtest jobs', {
                error: e instanceof Error ? e.message : String(e),
            });
            this.#dispatch({ type: 'FETCH_FAILED' });
            // Keep polling on transient errors so a single failure doesn't halt the loop.
            if (untracked(() => this.#state.value.polling)) {
                this.#scheduleNextPoll();
            }
        }
    }

    #scheduleNextPoll(): void {
        if (!untracked(() => this.#state.value.polling)) return;
        const intervalMs = this.#featureFlagService.getValue(backtestJobsListPollingIntervalMs);
        const timer = setTimeout(() => {
            void this.#fetch();
        }, intervalMs);
        this.#pollBox.next({ [Symbol.dispose]: () => clearTimeout(timer) });
    }
}
